import { TableActionButton, TableActionGroup } from "@channelplay/table-actions";
import { useCallback, useEffect, useId, useState } from 'react';
import { Info, Loader2, Plus, Users } from 'lucide-react';
import { incentiveAPI } from '../../services/api';
import Modal from '../../components/Modal';
import { ModalFormErrorBanner } from '../../components/ModalFormErrorBanner';
import {
  formatMultiplier, multiplierVersionFor, shortMonth,
  type DesignationMultiplierRow, type DesignationMultiplierVersion,
} from './types';

/**
 * Incentive › Plans: the DESIGNATION MULTIPLIER, one small card above the plans.
 *
 *   Designation multiplier · from Aug 2026 · iPro ×1.0 · Senior iPro ×1.3   [Edit]
 *
 * One project-wide setting on top of every plan, grid or per unit: what somebody
 * earned from sales is multiplied by the figure for their designation, before the
 * new-joiner guarantee. The explanation sits behind a hover, as on every other
 * figure of this page; the card itself is the number.
 *
 * It shows the version in force for the month chosen at the top of the page — or,
 * when none is yet (a month before the first version), the latest one, marked as not
 * in force. Saving with the same From month replaces that version; a later month
 * adds one, leaving the months before it on the older one; no rows removes it.
 */

const HOW_IT_WORKS = 'Multiplies what each person earned from sales by the figure for their '
  + 'designation, before the new-joiner guarantee. Applies to every plan. Designation is the '
  + '1Office budget name, as shown in Master Data › Employees, as it is NOW — 1Office keeps no '
  + 'history of it, so a past month uses today\'s designation. A designation not listed is ×1.';

interface Props {
  projectId: number;
  /** The month chosen at the top of the page, 'YYYY-MM'. */
  month: string;
}

interface DraftRow { designation: string; multiplier: string }

const rowsText = (rows: DesignationMultiplierRow[]): string =>
  rows.map((r) => `${r.designation} ${formatMultiplier(r.multiplier)}`).join(' · ');

export default function DesignationMultiplierCard({ projectId, month }: Props) {
  const [versions, setVersions] = useState<DesignationMultiplierVersion[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [from, setFrom] = useState('');
  const [rows, setRows] = useState<DraftRow[]>([]);
  const [designations, setDesignations] = useState<{ list: string[]; available: boolean } | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const listId = useId();

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await incentiveAPI.listDesignationMultipliers(projectId);
      setVersions(res.data.data?.versions ?? []);
    } catch (err: any) {
      setLoadError(err.response?.data?.message || 'Could not load the designation multiplier.');
    }
  }, [projectId]);

  useEffect(() => { void load(); }, [load]);

  const inForce = versions ? multiplierVersionFor(versions, month) : null;
  // Before the first version there is none in force: the latest one is shown instead.
  const shown = inForce ?? (versions && versions.length > 0 ? versions[versions.length - 1] : null);

  const openEditor = () => {
    setFrom(shown?.effectiveFrom ?? month);
    setRows(shown && shown.rows.length > 0
      ? shown.rows.map((r) => ({ designation: r.designation, multiplier: String(r.multiplier) }))
      : [{ designation: '', multiplier: '' }]);
    setSaveError(null);
    setEditing(true);
    if (designations === null) {
      incentiveAPI.listDesignations(projectId)
        .then((res) => setDesignations({
          list: res.data.data?.designations ?? [], available: res.data.data?.available !== false,
        }))
        .catch(() => setDesignations({ list: [], available: false }));
    }
  };

  const filled = rows.filter((r) => r.designation.trim() !== '' || r.multiplier.trim() !== '');
  const replaces = versions?.find((v) => v.effectiveFrom === from) ?? null;
  const removing = filled.length === 0;

  const save = async () => {
    if (!/^\d{4}-\d{2}$/.test(from)) { setSaveError('Choose the month it applies from.'); return; }
    setSaving(true);
    setSaveError(null);
    try {
      const res = await incentiveAPI.saveDesignationMultipliers(projectId, {
        effectiveFrom: from,
        // Sent as typed: the server says what is wrong with a row, in the words the
        // page would use, rather than this guessing at it.
        rows: filled.map((r) => ({
          designation: r.designation,
          multiplier: r.multiplier.trim() === '' ? null : Number(r.multiplier),
        })),
      });
      setVersions(res.data.data?.versions ?? []);
      setEditing(false);
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Could not save the designation multiplier.');
    } finally {
      setSaving(false);
    }
  };

  const allVersions = versions && versions.length > 1
    ? versions.map((v) => `From ${shortMonth(v.effectiveFrom)}: ${rowsText(v.rows)}`).join('\n')
    : undefined;

  return (
    <div
      className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm"
      data-testid="designation-multiplier-card"
      title={allVersions}
    >
      <Users className="h-4 w-4 flex-shrink-0 text-gray-400" />
      <span className="font-medium text-gray-900">Designation multiplier</span>
      <span title={HOW_IT_WORKS} aria-label="How the designation multiplier works" className="inline-flex cursor-help text-gray-400">
        <Info className="h-3.5 w-3.5" />
      </span>
      {loadError ? (
        <span className="text-red-700">· {loadError}</span>
      ) : versions === null ? (
        <span className="text-gray-400">· …</span>
      ) : shown ? (
        <>
          <span className="text-gray-600">· from {shortMonth(shown.effectiveFrom)}</span>
          {shown.rows.map((r) => (
            <span key={r.designation} className="text-gray-600">
              · {r.designation} <strong className="text-gray-900">{formatMultiplier(r.multiplier)}</strong>
            </span>
          ))}
          {!inForce && <span className="text-xs text-amber-700">(not in force for {shortMonth(month)})</span>}
          {versions.length > 1 && (
            <span className="text-xs text-gray-400">{versions.length} versions</span>
          )}
        </>
      ) : (
        <span className="text-gray-500">· none — everyone is paid as their plan says</span>
      )}
      <button
        type="button"
        onClick={openEditor}
        disabled={versions === null && !loadError}
        className="ml-auto rounded-lg border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
      >
        {shown ? 'Edit' : 'Set up'}
      </button>

      <Modal
        isOpen={editing}
        onClose={() => { if (!saving) setEditing(false); }}
        title="Designation multiplier"
        footer={
          <div className="flex w-full items-center justify-end gap-2">
            <button type="button" onClick={() => setEditing(false)} disabled={saving}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => { void save(); }}
              disabled={saving || (removing && !replaces)}
              className="flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {removing ? 'Remove this version' : 'Save'}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-xs text-gray-500">{HOW_IT_WORKS}</p>
          <label className="block">
            <span className="text-sm font-medium text-gray-900">From</span>
            <input
              type="month"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              aria-label="Applies from"
              className="mt-1 block rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
            />
            <span className="mt-1 block text-xs text-gray-500">
              {replaces
                ? `Replaces the version from ${shortMonth(replaces.effectiveFrom)}.`
                : 'A new version from this month on; earlier months keep what they had.'}
            </span>
          </label>

          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                <th className="py-1 pr-2 text-left">Designation</th>
                <th className="py-1 pr-2 text-right">Multiplier</th>
                <th className="py-1 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td className="py-1 pr-2">
                    <input
                      type="text"
                      list={listId}
                      value={r.designation}
                      placeholder="e.g. Senior iPro"
                      aria-label={`Row ${i + 1}: designation`}
                      onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, designation: e.target.value } : x)))}
                      className="w-full rounded border border-gray-200 px-2 py-0.5 text-sm"
                    />
                  </td>
                  <td className="py-1 pr-2 text-right">
                    <input
                      type="number" min={0.01} max={5} step="0.01"
                      value={r.multiplier}
                      placeholder="1.0"
                      aria-label={`Row ${i + 1}: multiplier`}
                      onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, multiplier: e.target.value } : x)))}
                      className="w-20 rounded border border-gray-200 px-2 py-0.5 text-right text-sm"
                    />
                  </td>
                  <td className="py-1 text-center"><TableActionGroup>
                    <TableActionButton action="remove" label="Remove this designation"
                      type="button"
                      title="Remove this designation"
                      onClick={() => setRows(rows.filter((_, j) => j !== i))} />
                  </TableActionGroup></td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={3} className="py-3 text-center text-xs text-gray-500">
                    No designations: saving removes this version.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <datalist id={listId}>
            {(designations?.list ?? []).map((d) => <option key={d} value={d} />)}
          </datalist>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setRows([...rows, { designation: '', multiplier: '' }])}
              className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Plus className="h-4 w-4" />
              Add a designation
            </button>
            <span className="text-xs text-gray-500">
              Above 0, at most 5. Not listed = ×1.
              {designations && !designations.available && ' Could not load the designations from 1Office — type them.'}
            </span>
          </div>
          {saveError && <ModalFormErrorBanner message={saveError} />}
        </div>
      </Modal>
    </div>
  );
}
